import React, { useEffect, useRef, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, Search, Database, Package, Tag, Save, Layers, Download, Upload, Loader2, Siren, Mail, Send, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/format";
import { Section, Skeleton, EmptyState, Pagination, usePagination } from "../components/UI";
import { EWS_STATUS, HET_STATUS, warnaEwsInput, warnaHetInput } from "../lib/ews";
import { IkonKomoditas } from "../components/EwsTable";

const TABS = [
  { key: "products", label: "Nama Produk", icon: Package },
  { key: "categories", label: "Kategori Produk", icon: Tag },
  { key: "jenis-produk", label: "Jenis Produk", icon: Layers },
  { key: "ews", label: "EWS", icon: Siren },
  { key: "email", label: "Email Notifikasi", icon: Mail },
];

export default function MasterDataPage() {
  const [activeTab, setActiveTab] = useState("products");

  return (
    <div className="space-y-6" data-testid="master-data-page">
      <header>
        <div className="kedan-label text-slate-500">Administrasi</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Master Data</h1>
        <p className="text-slate-500 text-sm mt-1">
          Kelola data master nama produk, kategori produk, jenis produk, harga EWS, dan push email notifikasi secara terpusat.
          Tersedia export dan import data melalui berkas CSV.
        </p>
      </header>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="master-data-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-[#045498] text-[#045498]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon className="w-4 h-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "products" && <MasterPanel type="products" label="Nama Produk" endpoint="/master-data/products" />}
      {activeTab === "categories" && <MasterPanel type="categories" label="Kategori Produk" endpoint="/master-data/categories" />}
      {activeTab === "jenis-produk" && <MasterPanel type="jenis-produk" label="Jenis Produk" endpoint="/master-data/jenis-produk" />}
      {activeTab === "ews" && <EwsPanel />}
      {activeTab === "email" && <EmailNotifPanel />}
    </div>
  );
}

/* ========== Early Warning System (EWS) ========== */
const EWS_KOSONG = { status_ews: "", het_persen: "", het_status: "" };

const samaDenganBaris = (d, row) =>
  (d.status_ews || "") === (row.status_ews || "") &&
  (d.het_persen || "") === (row.het_persen || "") &&
  (d.het_status || "") === (row.het_status || "");

function EwsPanel() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});     // { komoditas: { status_ews, het_persen, het_status } }
  const [pilihan, setPilihan] = useState({ status: EWS_STATUS, het: HET_STATUS });
  const [loading, setLoading] = useState(true);
  const [menyimpan, setMenyimpan] = useState("");
  const [pesan, setPesan] = useState({ type: "", text: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master-data/ews");
      const rows = data.items || [];
      setItems(rows);
      setPilihan({
        status: data.pilihan_status || EWS_STATUS,
        het: data.pilihan_het || HET_STATUS,
      });
      setDraft(Object.fromEntries(rows.map((i) => [i.komoditas, {
        status_ews: i.status_ews || "",
        het_persen: i.het_persen || "",
        het_status: i.het_status || "",
      }])));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ubahDraft = (komoditas, field, nilai) =>
    setDraft((d) => ({ ...d, [komoditas]: { ...(d[komoditas] || EWS_KOSONG), [field]: nilai } }));

  const simpan = async (row) => {
    const d = draft[row.komoditas] || EWS_KOSONG;
    setMenyimpan(row.komoditas);
    setPesan({ type: "", text: "" });
    try {
      await api.put("/master-data/ews", {
        komoditas: row.komoditas,
        status_ews: d.status_ews || null,
        het_persen: d.het_persen || null,
        het_status: d.het_status || null,
      });
      const kosong = !d.status_ews && !d.het_persen && !d.het_status;
      setPesan({
        type: "success",
        text: kosong ? `Data ${row.komoditas} dikosongkan.` : `Data ${row.komoditas} disimpan.`,
      });
      await load();
    } catch (e) {
      setPesan({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
    } finally { setMenyimpan(""); }
  };

  const terisi = items.filter((i) => i.status_ews || i.het_status || i.het_persen).length;

  return (
    <Section
      title="Early Warning System (EWS)"
      subtitle={`${items.length} komoditas pantauan · ${terisi} sudah diisi`}
    >
      <div className="kedan-card overflow-hidden" data-testid="ews-panel">
        <div className="px-5 py-4 border-b border-slate-200 text-xs text-slate-500">
          Status EWS dan status HET/HAP tampil sebagai running text pada menu Buat Pesanan.
          Kosongkan seluruh isian sebuah komoditas lalu simpan untuk menghapusnya dari running text.
        </div>

        {pesan.text && (
          <div className={`mx-5 mt-3 text-sm rounded-md px-3 py-2 ${
            pesan.type === "success"
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : "bg-red-50 border border-red-200 text-red-700"
          }`} data-testid="ews-msg">{pesan.text}</div>
        )}

        {loading ? (
          <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="Daftar komoditas kosong" description="Hubungi pengembang — daftar komoditas EWS bersifat tetap." icon={Siren} />
        ) : (
          <div className="overflow-x-auto">
            <table className="kedan-table">
              <thead>
                <tr>
                  <th>Komoditas</th>
                  <th className="w-48">Status EWS</th>
                  <th className="w-72">Status HET/HAP</th>
                  <th>Terakhir Diubah</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody data-testid="ews-table-body">
                {items.map((row) => {
                  const d = draft[row.komoditas] || EWS_KOSONG;
                  const berubah = !samaDenganBaris(d, row);
                  return (
                    <tr key={row.komoditas}>
                      <td className="font-medium whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <IkonKomoditas nama={row.komoditas} />
                          {row.komoditas}
                        </span>
                      </td>
                      <td>
                        <select
                          className={`kedan-input !py-1 text-sm ${warnaEwsInput(d.status_ews)}`}
                          value={d.status_ews}
                          onChange={(e) => ubahDraft(row.komoditas, "status_ews", e.target.value)}
                          data-testid={`ews-status-${row.komoditas}`}
                        >
                          <option value="">— Belum diisi —</option>
                          {pilihan.status.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <select
                            className={`kedan-input !py-1 text-sm flex-1 ${warnaHetInput(d.het_status)}`}
                            value={d.het_status}
                            onChange={(e) => ubahDraft(row.komoditas, "het_status", e.target.value)}
                            data-testid={`ews-het-status-${row.komoditas}`}
                          >
                            <option value="">— Belum diisi —</option>
                            {pilihan.het.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <input
                            type="text"
                            className="kedan-input !w-24 !py-1 text-sm"
                            placeholder="mis. 12,5%"
                            maxLength={20}
                            value={d.het_persen}
                            onChange={(e) => ubahDraft(row.komoditas, "het_persen", e.target.value)}
                            data-testid={`ews-het-persen-${row.komoditas}`}
                          />
                        </div>
                      </td>
                      <td className="text-xs text-slate-500">
                        {row.updated_at ? (
                          <>
                            {formatDateTime(row.updated_at)}
                            {row.updated_by && <div className="text-slate-400">oleh {row.updated_by}</div>}
                          </>
                        ) : <span className="text-slate-400">Belum diisi</span>}
                      </td>
                      <td className="text-right">
                        <button
                          onClick={() => simpan(row)}
                          disabled={!berubah || menyimpan === row.komoditas}
                          className={`kedan-btn-secondary !py-1 !px-2 text-xs ${!berubah ? "opacity-40 cursor-not-allowed" : ""}`}
                          data-testid={`ews-save-${row.komoditas}`}
                        >
                          {menyimpan === row.komoditas
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <><Save className="w-3.5 h-3.5" /> Simpan</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  );
}

function MasterPanel({ type, label, endpoint }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formNama, setFormNama] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");           // "export" | "import"
  const [notice, setNotice] = useState(null);     // { type, text }
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      console.error(`Failed to load ${type}:`, e);
    }
    setLoading(false);
  }, [endpoint, type]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormNama("");
    setError("");
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormNama(item.nama);
    setError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`${endpoint}/${editing.id}`, { nama: formNama });
      } else {
        await api.post(endpoint, { nama: formNama });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus "${item.nama}" dari ${label.toLowerCase()}?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      await load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const exportCsv = async () => {
    setBusy("export");
    setNotice(null);
    try {
      const res = await api.get(`${endpoint}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `master-${type}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setNotice({ type: "success", text: `${items.length} ${label.toLowerCase()} diekspor ke CSV.` });
    } catch (err) {
      setNotice({ type: "error", text: "Gagal mengekspor data." });
    }
    setBusy("");
  };

  const importCsv = async (file) => {
    if (!file) return;
    setBusy("import");
    setNotice(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`${endpoint}/import`, fd);
      const rincian = [
        `${data.ditambah} ditambahkan`,
        data.duplikat ? `${data.duplikat} duplikat dilewati` : null,
        data.baris_kosong ? `${data.baris_kosong} baris kosong` : null,
      ].filter(Boolean).join(" · ");
      setNotice({ type: data.ditambah ? "success" : "info", text: `Impor selesai — ${rincian}.` });
      await load();
    } catch (err) {
      setNotice({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    }
    setBusy("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const filtered = items.filter((item) =>
    !search || item.nama.toLowerCase().includes(search.toLowerCase())
  );

  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(filtered, 10);

  return (
    <div className="space-y-4" data-testid={`master-panel-${type}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{items.length}</span> {label.toLowerCase()} terdaftar
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={exportCsv} disabled={!!busy || items.length === 0} className="kedan-btn-secondary" data-testid={`export-${type}`}>
            {busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={!!busy} className="kedan-btn-secondary" data-testid={`import-${type}`}>
            {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import CSV
          </button>
          <input
            ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => importCsv(e.target.files?.[0])}
            data-testid={`import-input-${type}`}
          />
          <button onClick={openCreate} className="kedan-btn-primary" data-testid={`add-${type}`}>
            <Plus className="w-4 h-4" /> Tambah {label}
          </button>
        </div>
      </div>

      {notice && (
        <div
          className={`text-sm rounded-md px-3 py-2 flex items-start justify-between gap-3 ${
            notice.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
            : notice.type === "error" ? "bg-red-50 border border-red-200 text-red-700"
            : "bg-blue-50 border border-blue-200 text-blue-800"
          }`}
          data-testid={`notice-${type}`}
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="shrink-0 opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="kedan-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className="kedan-input !pl-9"
              placeholder={`Cari ${label.toLowerCase()}...`}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              data-testid={`search-${type}`}
            />
          </div>
          <div className="text-xs text-slate-500">{filtered.length} item</div>
        </div>

        {loading ? (
          <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={`Belum ada ${label.toLowerCase()}`}
            description={`Tambahkan ${label.toLowerCase()} untuk digunakan sebagai pilihan pada form produk supplier.`}
            icon={Database}
          />
        ) : (
          <>
            <table className="kedan-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>{label}</th>
                  <th>Dibuat</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody data-testid={`${type}-table-body`}>
                {paged.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-slate-500 text-sm">{(page - 1) * perPage + index + 1}</td>
                    <td className="font-medium text-slate-900">{item.nama}</td>
                    <td className="text-sm text-slate-500">{formatDate(item.created_at)}</td>
                    <td className="text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-600"
                          data-testid={`edit-${type}-${item.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(item)}
                          className="p-1.5 hover:bg-red-50 rounded text-red-600"
                          data-testid={`delete-${type}-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
          </>
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid={`${type}-modal`}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden animate-[scaleIn_0.2s_ease-out]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-slate-900">
                {editing ? `Edit ${label}` : `Tambah ${label}`}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>
                <input
                  className="kedan-input w-full"
                  required
                  value={formNama}
                  onChange={(e) => setFormNama(e.target.value)}
                  placeholder={`Masukkan ${label.toLowerCase()}...`}
                  data-testid={`form-${type}-nama`}
                  autoFocus
                />
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="kedan-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="kedan-btn-primary" data-testid={`form-${type}-submit`}>
                  <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : editing ? "Simpan" : "Tambah"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


/* ========== Push Email Notifikasi ========== */
function EmailNotifPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tujuan, setTujuan] = useState("");
  const [proses, setProses] = useState("");
  const [pesan, setPesan] = useState({ type: "", text: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/email-status");
      setStatus(data);
    } catch (err) {
      setPesan({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const kirimUji = async () => {
    setProses("uji");
    setPesan({ type: "", text: "" });
    try {
      const { data } = await api.post("/notifications/email-test", tujuan ? { email: tujuan } : {});
      setPesan({ type: "success", text: data.message });
      await load();
    } catch (err) {
      setPesan({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setProses("");
    }
  };

  const jalankanSiklus = async () => {
    setProses("siklus");
    setPesan({ type: "", text: "" });
    try {
      const { data } = await api.post("/notifications/email-run");
      setPesan({ type: "success", text: data.message });
      await load();
    } catch (err) {
      setPesan({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setProses("");
    }
  };

  if (loading) return <Skeleton className="h-64" />;
  if (!status) return <EmptyState title="Status email tidak tersedia" description="Coba muat ulang halaman." />;

  const siap = status.siap;
  const baris = [
    ["Transport", status.transport === "log" ? "log (uji coba, email tidak benar-benar dikirim)" : "SMTP"],
    ["Server SMTP", status.host ? `${status.host}:${status.port}` : "—"],
    ["Keamanan", status.security],
    ["Akun SMTP", status.user || "—"],
    ["Pengirim", status.from_email ? `${status.from_name} <${status.from_email}>` : "—"],
    ["Alamat aplikasi", status.app_url || "—"],
    ["Jadwal periksa", `setiap ${status.interval_menit} menit`],
    ["Maks. item per email", status.maks_item],
    ["Batas umur notifikasi", `${status.maks_umur_jam} jam`],
  ];

  return (
    <div className="space-y-4" data-testid="email-notif-panel">
      {pesan.text && (
        <div className={`p-3 text-sm rounded-lg border ${pesan.type === "error"
          ? "text-red-600 bg-red-50 border-red-100"
          : "text-emerald-600 bg-emerald-50 border-emerald-100"}`}>
          {pesan.text}
        </div>
      )}

      <Section
        title="Push Email Notifikasi"
        subtitle="Notifikasi yang masuk ke pusat notifikasi tiap pengguna dikirim juga ke alamat emailnya."
        action={
          <button onClick={load} className="kedan-btn-secondary" data-testid="email-refresh">
            <RefreshCw className="w-4 h-4" /> Muat Ulang
          </button>
        }
      >
        <div className="kedan-card p-5">
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${siap
          ? "bg-emerald-50 border-emerald-100"
          : "bg-amber-50 border-amber-100"}`}>
          {siap
            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <div className={`text-sm font-semibold ${siap ? "text-emerald-800" : "text-amber-800"}`}>
              {siap ? "Push email notifikasi aktif" : "Push email notifikasi belum aktif"}
            </div>
            {siap ? (
              <div className="text-xs text-emerald-700 mt-1">
                Sistem memeriksa notifikasi baru setiap {status.interval_menit} menit untuk {status.penerima_aktif} pengguna.
              </div>
            ) : (
              <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                {(status.masalah || []).map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <StatMini label="Terkirim 24 jam" value={status.terkirim_24j} tone="emerald" />
          <StatMini label="Gagal 24 jam" value={status.gagal_24j} tone={status.gagal_24j ? "red" : "slate"} />
          <StatMini label="Penerima aktif" value={status.penerima_aktif} tone="slate" />
          <StatMini label="Interval" value={`${status.interval_menit} mnt`} tone="slate" />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {baris.map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 text-slate-500 whitespace-nowrap w-56">{k}</td>
                  <td className="py-2 text-slate-800 font-medium break-all">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {status.error_terakhir && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 break-all">
            <span className="font-semibold">Error terakhir</span>
            {status.error_terakhir.updated_at ? ` (${formatDateTime(status.error_terakhir.updated_at)})` : ""}: {status.error_terakhir.error}
          </div>
        )}
        </div>
      </Section>

      <Section
        title="Uji Coba Pengiriman"
        subtitle="Kirim satu email percobaan untuk memastikan pengaturan SMTP pada berkas .env sudah benar."
      >
        <div className="kedan-card p-5">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            className="kedan-input flex-1"
            placeholder="Email tujuan (kosongkan untuk kirim ke email admin)"
            value={tujuan}
            onChange={(e) => setTujuan(e.target.value)}
            data-testid="email-test-input"
          />
          <button onClick={kirimUji} disabled={!!proses} className="kedan-btn-primary shrink-0" data-testid="email-test-send">
            {proses === "uji" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {proses === "uji" ? "Mengirim..." : "Kirim Email Uji"}
          </button>
          <button onClick={jalankanSiklus} disabled={!!proses} className="kedan-btn-secondary shrink-0" data-testid="email-run-cycle">
            {proses === "siklus" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Kirim Notifikasi Tertunda
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-3 leading-relaxed">
          Pengaturan diambil dari berkas <code className="font-mono">backend/.env</code>:
          <code className="font-mono"> EMAIL_NOTIF_ENABLED, EMAIL_TRANSPORT, SMTP_HOST, SMTP_PORT, SMTP_SECURITY,
          SMTP_USER, SMTP_PASSWORD, SMTP_FROM, APP_BASE_URL</code>. Setelah berkas diubah, backend perlu dijalankan ulang.
          Tiap pengguna dapat menonaktifkan email notifikasi sendiri melalui menu <b>Ubah Profil</b>.
        </p>
        </div>
      </Section>
    </div>
  );
}

function StatMini({ label, value, tone = "slate" }) {
  const warna = {
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    red: "bg-red-50 border-red-100 text-red-700",
    slate: "bg-slate-50 border-slate-100 text-slate-700",
  }[tone];
  return (
    <div className={`p-3 rounded-lg border ${warna}`}>
      <div className="text-lg font-display font-bold">{value ?? 0}</div>
      <div className="text-[11px] opacity-80 mt-0.5">{label}</div>
    </div>
  );
}
